import * as _ from 'lodash';
import { calculateColor } from './color';
import { calculateFont, isFontWithoutValue } from './font';
export class StyleService {
    constructor({ inEditor = true, styleParams = {}, siteTextPresets = {}, siteColors = [], fontCssUrls = [], } = {}) {
        this.SCOPES = {
            COLORS: 'colors',
            FONTS: 'fonts',
            NUMBERS: 'numbers',
        };
        this.fontCssUrls = [];
        // Saved styleParams
        this.getStyleParam = (path) => _.get(this.styleParams, path);
        this.getStyleParamByScope = (wixParam, scope) => this.getStyleParam(`${scope}.${wixParam}`);
        // Returns color from saved wix color palette
        this.getSiteColor = (reference) => _.find(this.siteColors, { reference });
        // Returns value from wix site text presets (default fonts)
        this.getSiteTextPreset = (themeName) => _.get(this.siteTextPresets, themeName);
        // Renders a directive to comply with wix-style (angular 1 style)
        this.renderStyleDirective = (wixParam, defaultValue) => wixParam
            ? `{{ style.${wixParam} ${defaultValue} }}`
            : `{{ ${defaultValue} }}`;
        // Render object { property, prefix, suffix } to css string
        this.toCSS = ({ property, prefix = '', suffix = '' }, color) => {
            const cssValue = [prefix, color.toString(), suffix]
                .filter((s) => s.length > 0)
                .join(' ');
            return `${property}: ${color && cssValue}`;
        };
        // Renders saved fonts urls
        this.getFontsUrls = () => 
        // _([_.get(this.styleParams, 'googleFontsCssUrl'), ...this.fontCssUrls])
        [this.styleParams?.googleFontsCssUrl, ...this.fontCssUrls]
            .filter((url) => !!url)
            .map((url) => `<link rel="stylesheet" type="text/css" href="${url}"/>`)
            .join('\n');
        this.isStyleValue = (value) => !_.isNil(value) && value !== false;
        // Render style.value if the style is an object (colors, fonts) or compute value from an object
        // Render style if the style is a primitive value (numbers)
        this.renderStyleValue = (style) => {
            if (_.isObjectLike(style)) {
                return isFontWithoutValue(style)
                    ? calculateFont(style)
                    : style.value.replace('spx/', 'px/');
            }
            return this.isStyleValue(style) && style;
        };
        // Returns either saved style if it's saved or default if param was not saved
        // Worst case, if no styles were saved, fallback to directive
        this.renderLiveSiteStyle = (savedStyle, defaultStyle, directive) => {
            if (this.isStyleValue(savedStyle)) {
                return this.renderStyleValue(savedStyle);
            }
            if (this.isStyleValue(defaultStyle)) {
                return this.renderStyleValue(defaultStyle);
            }
            return directive;
        };
        // Default getter by scope
        this.renderDefaultByScope = (scope, defaultValue) => (scope === this.SCOPES.COLORS && this.getSiteColor(defaultValue)) ||
            (scope === this.SCOPES.FONTS && this.getSiteTextPreset(defaultValue)) ||
            (scope === this.SCOPES.NUMBERS && defaultValue);
        // Render directive in editor or saved style in live site
        this.renderStyle = (wixParam, defaultValue, scope) => {
            const savedStyle = this.getStyleParamByScope(wixParam, scope);
            const defaultStyle = this.renderDefaultByScope(scope, defaultValue);
            const directive = this.renderStyleDirective(wixParam, defaultValue);
            return this.inEditor
                ? directive
                : this.renderLiveSiteStyle(savedStyle, defaultStyle, directive);
        };
        // Colors with opacity
        this.renderColorWithFixedOpacity = (color, opacity) => !_.isNil(color) && calculateColor(color, opacity);
        // Always applies initial fixedOpacity or initialOpacity for default value only
        this.renderSavedOrDefaultColorWithOpacity = (wixParam, defaultColorReference, initialOpacity, fixedOpacity) => {
            const savedColor = this.getStyleParamByScope(wixParam, this.SCOPES.COLORS);
            const defaultColor = this.getSiteColor(defaultColorReference);
            const color = this.renderLiveSiteStyle(savedColor, defaultColor);
            if (fixedOpacity) {
                return this.renderColorWithFixedOpacity(color, fixedOpacity);
            }
            return !_.isNil(savedColor)
                ? color
                : this.renderColorWithFixedOpacity(color, initialOpacity);
        };
        this.renderSiteColorWithOpacity = (defaultColorReference, opacity) => {
            const color = this.renderStyleValue(this.renderDefaultByScope(this.SCOPES.COLORS, defaultColorReference));
            return color && calculateColor(color, opacity);
        };
        // Fonts with initial size
        this.fontToFixedSize = (font, size) => `
    ${font};
    font-size: ${size}px;
    font-style: normal;`;
        this.renderFontWithFixedSize = (defaultThemeName, initialFontSize) => {
            const defaultStyle = this.renderStyleValue(this.getSiteTextPreset(defaultThemeName));
            return defaultStyle && this.fontToFixedSize(defaultStyle, initialFontSize);
        };
        // Public
        this.renderColor = (wixParam, defaultColorReference) => this.renderStyle(wixParam, defaultColorReference, this.SCOPES.COLORS);
        this.renderFont = (wixParam, defaultFontThemeName) => this.renderStyle(wixParam, defaultFontThemeName, this.SCOPES.FONTS);
        this.renderNumber = (wixParam, defaultValue) => this.renderStyle(wixParam, defaultValue, this.SCOPES.NUMBERS);
        this.renderFixedColor = (colorReference) => this.renderStyle(null, colorReference, this.SCOPES.COLORS);
        this.renderFixedFont = (fontPresetName) => this.renderStyle(null, fontPresetName, this.SCOPES.FONTS);
        this.getUploadedFontFaces = () => (this.styleParams && this.styleParams.uploadFontFaces) || [];
        this.renderFontUrls = () => this.getFontsUrls();
        // These helpers renders no directives because the mapping in editor should be done customly in frontend (this service can be used)
        // That is because custom opacity and font-sizes can't be rendered with directives
        this.renderColorWithOpacity = (wixParam, css, defaultColorReference, initialOpacity, fixedOpacity) => wixParam
            ? this.toCSS(css, this.renderSavedOrDefaultColorWithOpacity(wixParam, defaultColorReference, initialOpacity, fixedOpacity))
            : this.toCSS(css, this.renderSiteColorWithOpacity(defaultColorReference, fixedOpacity || initialOpacity));
        this.renderFontWithInitialSize = (wixParam, defaultThemeName, initialFontSize) => {
            const savedStyle = this.renderStyleValue(this.getStyleParamByScope(wixParam, this.SCOPES.FONTS));
            const defaultStyle = this.renderFontWithFixedSize(defaultThemeName, initialFontSize);
            const styleDirective = this.fontToFixedSize(this.renderStyleDirective(wixParam, defaultThemeName), initialFontSize);
            return this.renderLiveSiteStyle(savedStyle, defaultStyle, styleDirective);
        };
        this.inEditor = inEditor;
        this.styleParams = styleParams;
        this.siteTextPresets = siteTextPresets;
        this.siteColors = siteColors;
        this.fontCssUrls = fontCssUrls;
    }
}
//# sourceMappingURL=style.js.map